/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react';
import { config } from '@uhg-abyss/web/tools/config';
import {
  useParams,
  useLocation,
} from '@uhg-abyss/web/tools/reactRouterTools/reactRouterDom';
import { styled } from '@uhg-abyss/web/tools/styled';
import { storage } from '@uhg-abyss/web/tools/storage';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { Link } from '@uhg-abyss/web/ui/Link';
import { useReloadPopup } from '@src/state/reloadPopup/hooks';
import { PageLayout } from '@src/common/PageLayout';
import { Modal } from '@uhg-abyss/web/ui/Modal';
import { useUser } from '@src/state/user';
import { Brandmark } from '@uhg-abyss/web/ui/Brandmark';
import { LagoonContext } from '@src/AppContext/LagoonContext';
import { SessionTimeout } from './SessionTimeout';

const ContinueButton = styled('div', {
  width: '150px',
  padding: '6px',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  borderWidth: '2.5px',
  border: '2px solid $primary1',
  background: '$white',
  borderStyle: 'solid',
  borderRadius: '23px',
  marginTop: 'auto',
  marginBottom: '20px',
  alignItems: 'center',
  '&:hover': {
    cursor: 'pointer',
  },
});

const ContinueButtonText = styled('text', {
  color: '$primary1',
  fontWeight: 'bold',
  fontSize: '16px',
});

const CancelButtonView = styled('div', {
  '&:hover': {
    cursor: 'pointer',
  },
});

const CancelButton = styled('a', {
  width: '150px',
  padding: '6px',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'row',
  borderWidth: '2.5px',
  border: '2px solid $primary1',
  background: '$primary1',
  borderStyle: 'solid',
  borderRadius: '23px',
  marginTop: 'auto',
  marginBottom: '20px',
  alignItems: 'center',
  '&:hover': {
    cursor: 'pointer',
  },
  color: '$white!important',
  fontWeight: 'bold',
  marginRight: '12px',
  fontSize: '16px',
  textDecoration: 'none',
});

const HeaderStyle = styled('div', {
  boxShadow: '0 5px 5px 0 rgba(0,0,0,0.05)',
  borderRadius: '3px',
  borderBottom: '1px solid',
  borderColor: '$gray4',
  backgroundColor: '$primary2',
  height: 100,
  paddingTop: '20px',
  position: 'sticky',
  top: 0,
  zIndex: 100,
});

const Logo = styled('div', {
  width: '130px',
  height: '40px',
});

const RightSideLinks = styled('div', {
  textAlign: 'right',
});

const LinkStyle = styled('div', {
  display: 'inline-block',
});

const ReturnToDashboard = styled('div', {
  marginRight: '50px',
  display: 'inline-block',
});

const SignOutText = styled('text', {
  color: 'rgb(0 38 119)',
  fontWeight: 'bold',
  cursor: 'pointer',
  fontSize: '17px',
});

const ApiMarketplaceText = styled('a', {
  color: 'rgb(0 38 119)',
  fontWeight: 'bold',
  cursor: 'pointer',
});

export const Header = () => {
  const router = useRouter();
  const { token } = useParams();
  const { pathname } = useLocation();
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const { setReloadPopup } = useReloadPopup.actions();
  const { reloadPopup } = useReloadPopup.state();
  const { userUUID, isAPIMarketPlaceUser } = useUser.state();

  const lagoonContext = React.useContext(LagoonContext);
  const featureToggles = lagoonContext.getTable('feature-toggles');
  const uiMessages = lagoonContext.getTable('ui-messaging');
  const signout = () => {
    storage.session.remove('isApiMarketPlaceUser');
    setReloadPopup(false);
    setCancelModalOpen(true);
  };

  const cancelSignout = () => {
    setReloadPopup(true);
    setCancelModalOpen(false);
  };
  const alertUser = (e) => {
    if (reloadPopup && userUUID) {
      e.preventDefault();
      e.returnValue = '';
    }
  };

  useEffect(() => {
    window.addEventListener('beforeunload', alertUser);
    return () => {
      window.removeEventListener('beforeunload', alertUser);
    };
  }, [reloadPopup, userUUID]);

  const returnToDashboard = () => {
    router.navigate(`/${token}`);
  };

  return (
    <React.Fragment>
      <SessionTimeout />
      <HeaderStyle>
        <PageLayout>
          <Flex
            css={{
              justifyContent: 'space-between',
              gap: '50rem',
              margin: '0 auto',
              maxWidth: '1250px',
            }}
          >
            <Flex.Content space-50>
              <Logo>
                <Brandmark
                  affiliate="uhc"
                  variant="lockup"
                  color="blue"
                  brand="uhc"
                />
              </Logo>
            </Flex.Content>
            <Flex.Content space-50>
              <RightSideLinks>
                {pathname === '/confirmation' && (
                  <ReturnToDashboard>
                    <Link
                      color="$primary1"
                      aria-label="click here to return to dashboard"
                      onClick={returnToDashboard}
                    >
                      <strong>Return to Dashboard</strong>
                    </Link>
                  </ReturnToDashboard>
                )}
                <LinkStyle>
                  {isAPIMarketPlaceUser ? (
                    <ApiMarketplaceText
                      href={`${
                        featureToggles.getRow('API_MARKETPLACE_WELCOME_URL')
                          ?.active || config('API_MARKETPLACE_WELCOME_URL')
                      }`}
                      role="button"
                    >
                      Return to API Marketplace
                    </ApiMarketplaceText>
                  ) : (
                    <SignOutText onClick={signout}>Sign out</SignOutText>
                  )}
                </LinkStyle>
              </RightSideLinks>
            </Flex.Content>
          </Flex>
          <Modal
            isOpen={cancelModalOpen}
            onClose={cancelSignout}
            width="465px"
            style={{ padding: '10px' }}
            title="Are you sure you want to sign out?"
            size="md"
          >
            <Modal.Section>
              <p>
                If you sign out, your changes will <strong>not</strong> be
                saved.
              </p>
            </Modal.Section>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                padding: '0 20px',
              }}
            >
              <ContinueButton onClick={cancelSignout}>
                <ContinueButtonText>No, Continue</ContinueButtonText>
              </ContinueButton>
              <CancelButtonView>
                <CancelButton
                  href={`${
                    uiMessages.getRow('OIDC_LOGOUT_API_URL')?.message ||
                    config('OIDC_LOGOUT_API_URL')
                  }`}
                  role="button"
                >
                  Yes
                </CancelButton>
              </CancelButtonView>
            </div>
          </Modal>
        </PageLayout>
      </HeaderStyle>
    </React.Fragment>
  );
};
